import _ from 'lodash'
import geohash from 'ngeohash'

import {
  IEventStaffInterface,
  IEventStaffInterfaceTypes as iesit,
} from './event-staff/event-staff.interface'
import { INotificationSettings, ISelect, ITeamMember, IUserInterface } from './users/user.interface'

import helpers from '../commonHelpers/helpers'
import { CONST } from '../const/const'
import { CountryList } from '../fakeData/countryList'
import FirestoreService from '../services/firestoreService'
import { EventReviewPublishModel } from './event-review-publish/event-review-publish.model'
import { EventModel } from './events/event.model'
import { getUserFullName } from '../helpers/helpers'
import { EventFeesModel } from './event-fees/event-fees.model'
import { EventStaffModel } from './event-staff/event-staff.model'
import { EventTicketingModel } from './event-ticketing/event-ticketing.model'
import { EventDetailsModel } from './event-details/event-details.model'
import { EventSchedulingModel } from './event-scheduling/event-scheduling.model'
import { EventPoliciesModel } from './event-policies/event-policies.model'
import { EventPaperworkModel } from './event-paperwork/event-paperwork.model'
import EventPaymentSettingsModel from './event-payment-settings/event-payment-settings.model'
import { IUserPublicInterface } from './users-public/users-public.interface'
import { ITeamMemberR } from './event-registered-users/event-registered-users.interface'

export interface IDateInterface {
  seconds: number
  nanoseconds: number
}

interface IUserExtendedWithSelected extends IUserInterface {
  selected?: boolean
}

type IArgObj = Record<any | IDateInterface, any | IDateInterface>
type IGetValueFromListType = (args: {
  list: ISelect[]
  valueToFind: string
  valueToGet?: 'label' | 'value'
}) => ISelect | undefined | null
type IGetStructuredTeamMembersList = (teamMembers: ITeamMember[]) => ITeamMember[]

// Constants
const staffCategoriesArray = [
  {
    title: 'Management',
    category: ['show manager', 'secretary', 'course designers', 'technical delegate', 'announcers'],
    logo: '/assets/cp_icons/MySpace-1.svg',
  },
  {
    title: 'Judges & scorers',
    category: ['judges'],
    logo: '/assets/cp_icons/Law-1.svg',
  },
  {
    title: 'Stewards & ingates',
    category: ['stewards', 'ingates'],
    logo: '/assets/cp_icons/Paste-1.svg',
  },
  {
    title: 'Veterinarians & farriers',
    category: ['farrier', 'veterinarian'],
    logo: '/assets/cp_icons/Heart-1.svg',
  },
  {
    title: 'Volunteers',
    category: ['volunteers'],
    logo: '/assets/cp_icons/Cancer Ribbon-1.svg',
  },
]

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
const getNotificationSettings = (obj?: INotificationSettings) => {
  return {
    newMessages: obj?.newMessages ?? false,
    weatherUpdates: obj?.weatherUpdates ?? false,
    resultUpdates: obj?.resultUpdates ?? false,
    courseUpdates: obj?.courseUpdates ?? false,
    paymentUpdates: obj?.paymentUpdates ?? false,
  }
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/* Calculate the upper and lower boundary geohashes for
 a given latitude, longitude, and distance in miles */
const getGeohashRange = (
  latitude: number,
  longitude: number,
  distance: number // miles
) => {
  const lat = 0.0144927536231884 // degrees latitude per mile
  const lon = 0.0181818181818182 // degrees longitude per mile

  const lowerLat = latitude - lat * distance
  const lowerLon = longitude - lon * distance

  const upperLat = latitude + lat * distance
  const upperLon = longitude + lon * distance

  const lower = geohash.encode(lowerLat, lowerLon)
  const upper = geohash.encode(upperLat, upperLon)

  return {
    lower,
    upper,
  }
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
const getUtcDate = (date?: Date | string): string => {
  let currentUtcDate: Date = new Date()
  if (date) currentUtcDate = new Date(date)
  const dateString =
    currentUtcDate.getUTCFullYear() +
    '-' +
    ('0' + (currentUtcDate.getUTCMonth() + 1)).slice(-2) +
    '-' +
    ('0' + currentUtcDate.getUTCDate()).slice(-2) +
    'T' +
    ('0' + currentUtcDate.getUTCHours()).slice(-2) +
    ':' +
    ('0' + currentUtcDate.getUTCMinutes()).slice(-2) +
    ':' +
    ('0' + currentUtcDate.getUTCSeconds()).slice(-2) +
    'Z'

  return dateString
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/**
 * @param start The number from where to start
 * @param end The number where to end
 * @return array of numbers in that range
 */
const getNumbersArrBetween = (start: number, end: number): number[] => {
  let valueToReturn: number[] = []
  if (isNaN(start) || isNaN(end)) return valueToReturn
  valueToReturn.push(start)
  for (let i = start; i <= end; i++) valueToReturn.push(i + 1)
  return valueToReturn
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/** Converts all nt type dates to normal strings and returns obj with normal dates */
const getConvertedData = <Type extends IArgObj>(argsObj: Type, debugValue?: string): Type => {
  let finalObj = {}
  let objIsValid = argsObj && typeof argsObj === 'object'
  let objIsArray = Array.isArray(argsObj)

  if (typeof argsObj === 'string') return argsObj

  if (objIsValid && !objIsArray && !Object.keys(argsObj).length) return argsObj

  if (objIsValid && objIsArray) {
    if (debugValue === 'debug')
      argsObj = argsObj.map((curr: any) => {
        return getConvertedData({ curr }).curr
      })
    return argsObj
  }

  if (argsObj) {
    const objKeys = Object.keys(argsObj)
    const objValues = Object.values(argsObj)

    const isDate = (value: any) => {
      return value instanceof Date && _.isDate(value)
    }

    const getDate = (value: IDateInterface) => {
      if (value?.seconds) {
        value = `${new Date(value?.seconds * 1000)}` as any
      }
      value = `${new Date(value as any)}` as any
      return value
    }

    objKeys.forEach((curr, index) => {
      let value = objValues[index]

      if (typeof value === 'object' && value !== null && !Array.isArray(value))
        value = getConvertedData(value)
      if (Array.isArray(value)) value = value.map((currValue) => getConvertedData(currValue))

      let isTypeOfDate = isDate(value)

      if (value?.seconds || isTypeOfDate) {
        try {
          value = getDate(value)
        } catch (err) {
          value = `${new Date()}` as any
        }
      }

      finalObj = {
        ...finalObj,
        [curr]: value,
      }
    })
  }
  return finalObj as Type
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/**
 * @param any Return date valid to react date picker
 */
const getReactPickerDate = (date: any) => {
  let date_
  let { date: date_copy } = getConvertedData({ date })
  try {
    if (date) {
      date_ = new Date(date_copy)
      if (date_ && isNaN(date_?.getDate())) return null
    } else throw new Error('Date not provided')
  } catch (err) {
    date_ = null
  }

  return date_
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/** Returns date of acc to firestore */
const getFirestoreDate = <Type extends Date>(
  data?: Type | null,
  allowAssignmentOfCurrDate?: boolean
): Type => {
  let dataToReturn
  if (!data) dataToReturn = allowAssignmentOfCurrDate === false ? '' : new Date()
  else {
    try {
      if ((data as any)?.seconds) {
        dataToReturn = new Date((data as any)?.seconds * 1000)
      } else dataToReturn = data ? new Date(`${data}`) : new Date(data)
    } catch (error) {
      dataToReturn = new Date()
    }
  }

  return dataToReturn as Type
}

const getPublishedDetailsOfEvent = async (eventId: string) => {
  try {
    const doc = await FirestoreService.getItem(
      CONST.DATA.FIRESTORE.V01.COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
      eventId
    )

    const eventSnap = await FirestoreService.getItem(
      CONST.DATA.FIRESTORE.V01.COLLECTIONS.EVENTS.NAME,
      eventId
    )

    const event = EventModel.fromFirestoreDoc(eventSnap).toObject()
    const selectedEventDetails = EventReviewPublishModel.fromFirestoreDoc(doc).toObject()
    const normalizedPublishDoc = getConvertedData(selectedEventDetails)

    return {
      basicEventDetails: getConvertedData({
        id: event.id,
        owner: event.owner,
        created: event.created,
        modified: event.modified,
        status: event.status ?? null,
        description: normalizedPublishDoc.EventDetails?.summaryLong ?? '',
        registrationPrice: null,
        registrationPriceAlias: null,
      }),
      Event: getConvertedData(event),
      EventFees: getConvertedData(new EventFeesModel(normalizedPublishDoc.EventFees).toObject()),
      EventStaff: getConvertedData(new EventStaffModel(normalizedPublishDoc.EventStaff).toObject()),
      EventTickets: getConvertedData(
        new EventTicketingModel(normalizedPublishDoc.EventTickets).toObject()
      ),
      EventDetails: getConvertedData(
        new EventDetailsModel(normalizedPublishDoc.EventDetails).toObject()
      ),
      EventSchedule: getConvertedData(
        new EventSchedulingModel(normalizedPublishDoc.EventSchedule).toObject()
      ),
      EventPolicies: getConvertedData(
        new EventPoliciesModel(normalizedPublishDoc.EventPolicies).toObject()
      ),
      EventPaperwork: getConvertedData(
        new EventPaperworkModel(normalizedPublishDoc.EventPaperwork).toObject()
      ),
      EventPriceList: getConvertedData(event),
      EventRegisteredUsers: [],
      EventRecipients: [],
      EventSpectators: [],
      EventRegistrationTickets: [],
      EventPaymentSettings: getConvertedData(
        new EventPaymentSettingsModel(normalizedPublishDoc.EventPaymentSettings).toObject()
      ),
      EventRequiredFields: {
        requiredFields: event.requiredFields || [],
        requiredHorseFields: event.requiredHorseFields || [],
      },
      EventQuestions: getConvertedData(normalizedPublishDoc.EventQuestions),
    }
  } catch (error) {
    console.error(error, 'error')
    return null
  }
}

function calculateAge(birthday?: string | Date | null) {
  if (!birthday) return 0
  const currentTime = new Date().getTime()
  const birthDateTime = new Date(birthday).getTime()
  const difference = currentTime - birthDateTime
  const ageInYears = difference / (1000 * 60 * 60 * 24 * 365)
  return Math.floor(ageInYears)
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/** Returns code from select obj */
const selectObjToString = <Type extends any>(selectObj: Type): string => {
  let valueToReturn: any = ''
  let selectObjCopy = selectObj as ISelect

  if (selectObjCopy?.value) valueToReturn = selectObjCopy?.value
  else if (typeof selectObjCopy === 'string') valueToReturn = selectObjCopy
  else if (typeof Array.isArray(selectObjCopy)) valueToReturn = selectObjCopy
  else valueToReturn = ''

  return valueToReturn ?? ''
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/**
 * @info Takes obj having select values
 * @return Obj having string values
 */
const objsSelectValuesToString = <Type extends Record<any, any>>(ObjWithSelectVals: Type): Type => {
  let originalValue = ObjWithSelectVals
  let valueToReturn: any = {}

  if (!!!ObjWithSelectVals) return originalValue

  if (typeof ObjWithSelectVals === 'object' && Object.keys(ObjWithSelectVals).length) {
    valueToReturn = Object.keys(ObjWithSelectVals).reduce(
      (acc: any, currKey: any) => {
        if (typeof ObjWithSelectVals[currKey] === 'object') {
          acc[currKey] = selectObjToString(ObjWithSelectVals[currKey])
        } else acc[currKey] = ObjWithSelectVals[currKey]
        return acc
      },
      { ...ObjWithSelectVals }
    )
  }
  return typeof valueToReturn === 'object' && Object.keys(valueToReturn).length === 0
    ? originalValue
    : valueToReturn
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/** Returns selected value of react select */
const getValueFromSelectList: IGetValueFromListType = ({ list, valueToFind, valueToGet }) => {
  const foundValue = _.find(list, { value: valueToFind })
  if (!foundValue) return null
  else if (valueToGet && foundValue[valueToGet]) return foundValue[valueToGet]
  else return foundValue
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/**
 * @param teamMembers Takes an array of team members or an empty array
 * @return Structured team members
 */
const getStructuredTeamMembersList: IGetStructuredTeamMembersList = (teamMembers) => {
  let dataToReturn: ITeamMember[]
  dataToReturn = teamMembers
    .filter((teamMember) => teamMember?.memberId && teamMember.memberId !== '')
    .map((currTeamMember) => {
      return {
        memberId: currTeamMember?.memberId,
        connectedDate: currTeamMember.connectedDate ?? '',
        memberRole: currTeamMember?.memberRole ?? '',
        defaultRole: currTeamMember?.defaultRole ?? '',
        memberDob: currTeamMember?.memberDob ?? 'unknown',
        memberName: currTeamMember?.memberName ?? 'unknown',
        memberEmail: currTeamMember?.memberEmail ?? 'unknown',
        memberAddress: currTeamMember?.memberAddress ?? 'unknown',
        memberPhoneNumber: currTeamMember?.memberPhoneNumber ?? '',
        memberProfilePicture: currTeamMember?.memberProfilePicture ?? '',
        memberCountry:
          (getValueFromSelectList({
            list: CountryList,
            valueToFind: currTeamMember?.memberCountry ?? '',
            valueToGet: 'label',
          }) as string) ??
          currTeamMember?.memberCountry ??
          '',
      }
    })

  return dataToReturn
}

const getSelectedUserAsTeamMember = (
  user: IUserExtendedWithSelected,
  registrar: IUserPublicInterface
): ITeamMemberR => {
  return {
    userId: registrar.id,
    userName: getUserFullName(registrar) ?? null,
    userNameNGram: registrar.userNameNGram || [],
    memberProfilePicture: user.userProfilePicture || null,
    memberDob: user.userDOB || null,
    memberId: user.id,
    memberName: getUserFullName(user),
    memberNameNGram: user.userNameNGram || [],
    memberRole: user.userDefaultRole,
    memberEmail: user.userEmail,
    memberCountry: CountryList.find((curr) => curr.value === user.userNationality)?.label || null,
    selected: true,
  }
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
/**
 * Returns a UTC timestamp,
 */
const utcTimestamp = () => {
  return new Date().toUTCString()
}

const arrangeEventstaff = (EventStaff: IEventStaffInterface) => {
  let found: boolean = false
  let foundOnIndex: number = -1
  let icon: string | null = null
  let exitsInCategories: boolean = false
  let foundCategoryTitle: string | null = null
  let eventStaff = EventStaff.eventStaff ?? []
  let otherStaff = EventStaff.otherStaff ?? []
  let staffList: iesit['IStaffUiListItem'][] = []
  let staffListItem: iesit['IStaffUiListItem'] | null = null

  eventStaff.forEach((staffItem) => {
    icon = null
    foundOnIndex = -1
    exitsInCategories = !!staffCategoriesArray.find((currCategory, index) => {
      if (currCategory.category.includes(staffItem.title.toLocaleLowerCase())) {
        foundOnIndex = index
        icon = currCategory.logo
        foundCategoryTitle = currCategory.title
        return true
      }
      return false
    })

    found = foundOnIndex !== -1

    if (exitsInCategories && found) {
      staffItem.value.forEach((currStaffId) => {
        foundOnIndex = -1

        if (typeof currStaffId === 'string' && !!currStaffId) {
          staffListItem =
            staffList.find((currItem, currItemIndex) => {
              if (currItem.category.toLocaleLowerCase() !== staffItem.title.toLocaleLowerCase())
                return false
              foundOnIndex = currItemIndex
              return true
            }) ?? null

          found = foundOnIndex !== -1

          if (!staffListItem) {
            staffList.push({
              icon,
              category: helpers.capitalizeFirstLetter(foundCategoryTitle ?? '', true),
              staffs: [
                ...staffItem.value.map((currValue) => {
                  return {
                    staffId: currValue,
                    staffType: staffItem.title,
                  }
                }),
              ],
            })
          } else if (found) {
            staffList[foundOnIndex] = {
              ...staffList[foundOnIndex],
              staffs: [...staffList[foundOnIndex].staffs, ...staffItem.value] as any[],
            }
          }
        }
      })
    }
  })

  otherStaff.forEach((otherStaffItem) => {
    otherStaffItem.value.forEach((currStaffId) => {
      if (currStaffId) {
        // if (indexToAddOn === -1) indexToAddOn = staffList.length
        staffList.push({
          category: 'Other staffs',
          icon: staffCategoriesArray[0].logo,
          staffs: [
            ...(staffList[foundOnIndex]?.staffs ?? []),
            ...otherStaffItem.value.map((currValue) => {
              return {
                staffId: currValue,
                staffType: otherStaffItem.title,
              }
            }),
          ] as any,
        })
      }
    })
  })

  staffList = staffList.reduce(
    (acc: iesit['IStaffUiListItem'][], currItem) => {
      found = false
      foundOnIndex = -1
      acc.find((curr, index) => {
        if (curr.category === currItem.category) {
          foundOnIndex = index
          found = true
          return true
        }
        return false
      })

      if (found && acc?.[foundOnIndex]) {
        acc[foundOnIndex] = {
          ...acc[foundOnIndex],
          staffs: [...(acc[foundOnIndex].staffs || []), ...((currItem.staffs || []) as any)],
        }
      } else {
        acc.push({
          icon: currItem.icon,
          staffs: currItem.staffs,
          category: currItem.category,
        })
      }

      return acc
    },
    [] as iesit['IStaffUiListItem'][]
  )

  return staffList
}

const isMinorFn = (dob?: Date | string | null): boolean => {
  const MINORITY_AGE = 18
  let valueToReturn = false

  if (!dob) return valueToReturn

  return calculateAge(dob) < MINORITY_AGE
}

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%

export const interfaceHelper = {
  getNotificationSettings,
  getFirestoreDate,
  getReactPickerDate,
  getStructuredTeamMembersList,
  getConvertedData,
  utcTimestamp,
}

export {
  isMinorFn,
  utcTimestamp,
  arrangeEventstaff,
  calculateAge,
  getConvertedData,
  getGeohashRange,
  getNumbersArrBetween,
  getPublishedDetailsOfEvent,
  getReactPickerDate,
  getUtcDate,
  getValueFromSelectList,
  objsSelectValuesToString,
  selectObjToString,
  getSelectedUserAsTeamMember,
}
